import { MotionBlurFilter } from "@pixi/filter-motion-blur";
import * as Logger from "js-logger";
import { Signal } from "signals";
import SymbolBehaviourDriver, { SymbolBehaviourMethod } from "slotworks/components/matrix/symbol/behaviours/symbol-behaviour-driver";
import { default as GraphicsManager, default as graphicsManager } from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import { CenterPivot } from "slotworks/graphics/pixi/group";
import { setTransformRect } from "slotworks/graphics/pixi/pixi-utils";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import SlotDefinition from "slotworks/model/slot-definition";
import SymbolDefinition from "slotworks/model/symbol-definition";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import { getOffsetRectangle } from "slotworks/utils/position-offset-calculator";
import symbolPool from "slotworks/utils/symbol-static-pool";
import AbstractSymbolBehaviour from "./behaviours/abstract-symbol-behaviour";

export default class SymbolComponent extends SymbolBehaviourDriver {

    public static maxMotionBlur: number = 0;

    /**
     * Position in matrix where {x:1, y:1} if top left
     */
    public readonly gridPosition: Point;
    public readonly landSignal: Signal;

    public symbolDefinition: SymbolDefinition;

    /**
     * The target transform of a "normal" symbol.
     * Note this is always in terms of the MatrixContent layer
     */
    private transform: Rectangle;
    private visible: boolean = true;
    private motionBlurFilter: MotionBlurFilter;

    // TODO: Perhaps promote this to somewhere globally accessible
    private masterPosition: string = "symbol_1_1";

    private staticSprite: PIXI.Sprite;
    private staticSpriteBlurred: PIXI.Sprite;

    private stackOffset: number = 0;
    private isStackPart: boolean;

    private waitingToStick: boolean;
    private moving: boolean;
    private blurred: boolean;

    private staticSprites: Map<string, PIXI.Sprite>;
    private staticSpritesBlurred: Map<string, PIXI.Sprite>;
    private staticSpriteOffsets: Map<string, Rectangle>;

    private layerLocked: boolean;
    private layer: Layers;
    private container: PIXI.Container;
    private matrixLayer: Layers;
    private animationLayer: Layers;

    private maskGraphics: PIXI.Graphics;

    constructor(gridPosition: Point, matrixLayer: Layers, animationLayer: Layers) {
        super();

        this.matrixLayer = matrixLayer;
        this.animationLayer = animationLayer;

        this.landSignal = new Signal();

        this.gridPosition = gridPosition.clone();

        this.generateStaticSprites();

        this.setVisible(true);

        try {
            if (SymbolComponent.maxMotionBlur > 0) {
                this.motionBlurFilter = new MotionBlurFilter([0, 0], 11);
                this.staticSprite.parent.filters = [this.motionBlurFilter];
            }
        } catch (e) {
            Logger.warn("Motion blur filter not supported");
        }
    }

    public destroy() {
        if (this.container && this.container.parent) {
            this.container.parent.removeChild(this.container);
        }

        this.removeAllBehaviours();

        symbolPool.releaseStaticSprites(this.staticSprites);
        symbolPool.releaseStaticBlurredSprites(this.staticSpritesBlurred);
    }

    public static() {
        if (this.waitingToStick) {
            this.stick().execute();
        } else {
            this.executeBehaviour(SymbolBehaviourMethod.Static).execute();
        }
    }

    public land(): Contract<void> {
        Services.sound.event(SoundEvent.symbol_ID_land, this.symbolId);

        if (this.symbolDefinition.autoStick) {

            if (this.visible && !this.proxy) {
                Services.sound.event(SoundEvent.stick_symbol);
                Services.sound.event(SoundEvent.stick_symbol_ID, this.symbolId);
            }

            this.waitingToStick = true;
        }

        this.landSignal.dispatch();

        return new Contract<void>((resolve) => {
            this.executeBehaviour(SymbolBehaviourMethod.Land).then(() => {
                if (this.waitingToStick) {
                    this.stick().execute();
                }
                resolve(null);
            });
        });
    }

    public setMoving(moving: boolean) {
        this.moving = moving;
    }

    public setBlurred(blurred: boolean) {
        this.blurred = blurred;
    }

    public isMoving() {
        return this.moving;
    }

    public stick() {
        this.waitingToStick = false;
        return this.executeBehaviour(SymbolBehaviourMethod.Stick);
    }

    public expand(x: number = 0, y: number = 0): Contract<void> {
        Services.sound.event(SoundEvent.symbol_any_expand);
        Services.sound.event(SoundEvent.symbol_ID_expand, this.symbolId);
        if (y < 0) {
            Services.sound.event(SoundEvent.symbol_any_expand_up);
            Services.sound.event(SoundEvent.symbol_ID_expand_up, this.symbolId);
        } else if (y > 0) {
            Services.sound.event(SoundEvent.symbol_any_expand_down);
            Services.sound.event(SoundEvent.symbol_ID_expand_down, this.symbolId);
        }

        return this.executeBehaviour(SymbolBehaviourMethod.Expand, null, x, y);
    }

    public addBehaviourOfType(behaviourType: typeof AbstractSymbolBehaviour) {
        const behaviour = new behaviourType(this);
        this.addBehaviour(behaviour);
    }

    public stop(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Stop);
    }

    public highlight(result?: SymbolWinResult): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Highlight, result);
    }

    public highlightDim(result?: SymbolWinResult): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.HighlightDim, result);
    }

    public winDim(result?: SymbolWinResult): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.WinDim, result);
    }

    public cycleDim(result?: SymbolWinResult): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.CycleDim, result);
    }

    public win(result?: SymbolWinResult): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Win, result);
    }

    public cycle(result?: SymbolWinResult): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Cycle, result);
    }

    public landAnticipate(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.LandAnticipate);
    }

    public anticipate(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Anticipate);
    }

    public remove(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Remove);
    }

    public setSymbol(id: string) {
        this.symbolId = id;

        this.symbolDefinition = SlotDefinition.getSymbolDefinition(this.symbolId);

        this.executeBehaviour(SymbolBehaviourMethod.SymbolChange).execute();
    }

    public symbolExists(id: string) {
        return this.staticSprites.has(id);
    }

    public refresh() {
        this.setSymbol(this.symbolId);
    }

    /**
     * Sets the position and size of the static symbol
     * Anchor point is always centered
     *
     * @param transform {Rectangle}
     */
    public setTransform(transform: Rectangle) {
        this.transform = transform;
    }

    /**
     * The current speed at which the symbol is travelling, btween 0 and 1, 1 being an entire symbol position
     */
    public setVelocity(velocity: number) {
        if (this.motionBlurFilter) {
            (this.motionBlurFilter.velocity as any) = [0, SymbolComponent.maxMotionBlur * velocity];
        }
    }

    public offsetStack(offset: number) {
        this.stackOffset = offset;
    }

    public setIsStackPart(stackPart: boolean) {
        this.isStackPart = stackPart;
        this.updateVisible();
    }

    public isPartOfStack() {
        return this.isStackPart;
    }

    public getVisible() {
        return this.visible;
    }

    /**
     * Sets visibility for the static symbol
     * @param visible {boolean}
     */
    public setVisible(visible: boolean) {
        this.visible = visible;
        this.updateVisible();
    }

    public tint(color: number) {
        const tintElementMaps = [this.staticSprites];

        for (const elementMap of tintElementMaps) {
            elementMap.forEach((element) => {
                if (element) {
                    element.tint = color;
                }
            });
        }
    }

    public setScale(scale: Point) {
        this.staticSprite.scale.x = scale.x;
        this.staticSprite.scale.y = scale.y;
    }

    public maskAtPosition(x: number, y: number) {
        const position = this.matrixLayer.getPosition(`symbol_${x}_${y}`);
        this.maskGraphics = new PIXI.Graphics();
        this.maskGraphics.beginFill(0xFF3300);
        this.maskGraphics.drawRect(position.x, position.y, position.width, position.height);
        this.maskGraphics.endFill();

        this.setMask(this.maskGraphics);
        this.container.addChild(this.maskGraphics);
    }

    public removeMask() {
        this.container.removeChild(this.maskGraphics);
    }

    public setMask(mask: PIXI.Sprite | PIXI.Graphics) {
        this.container.mask = mask;
    }

    public setLayer(layer: Layers) {
        if (!this.layerLocked) {
            this.layer = layer;

            this.layer.add(this.container);
        }
    }

    public moveToTop() {
        if (this.layer && this.layer === this.matrixLayer) {
            this.layer.add(this.container);
        }
    }

    public getZIndex() {
        return this.container.parent.getChildIndex(this.container);
    }

    public lockLayer() {
        this.layerLocked = true;
    }

    public getLayer() {
        return this.layer;
    }

    public updateTransform() {

        // Translate transform to local co-ordinate space
        let transform = this.transform;

        if (this.layer !== this.matrixLayer) {
            const globalPosition = this.matrixLayer.localToGlobal(transform);
            transform = this.layer.globalToLocal(globalPosition);
        }

        this.updateStatic(transform);

        // Offset stacks
        if (this.stackOffset !== 0) {
            this.staticSprite.y -= this.getStackOffsetY();
        }

        this.updateVisible();

        this.executeBehaviour(SymbolBehaviourMethod.UpdateTransform).execute();
    }

    public getTransform(): Rectangle {
        return this.transform;
    }

    /**
     * Returns a perctange of the stack this belongs to. For example if
     * this is the middle symbol of a 3-high stack, this will return 0.3.
     * If it's the bottom symbol of a 3-high stack, this will return 0.6
     */
    public getStackOffsetY(): number {

        const referenceSymbolPosition = this.matrixLayer.getPosition(`symbol_${this.gridPosition.x}_1`);
        const offsetReferenceSymbolPosition = this.matrixLayer.getPosition(`symbol_${this.gridPosition.x}_2`);

        let spacing = 0;

        if (offsetReferenceSymbolPosition) {
            spacing = offsetReferenceSymbolPosition.y - referenceSymbolPosition.y - referenceSymbolPosition.height;
        }

        const singleSymbolOffset = referenceSymbolPosition.height + spacing;

        return this.stackOffset * singleSymbolOffset;
    }

    public getStackOffset(): number {
        return this.stackOffset;
    }

    public getMatrixLayer() {
        return this.matrixLayer;
    }

    public getAnimationLayer() {
        return this.animationLayer;
    }

    public createStaticCopy() {
        const sprite = graphicsManager.createSprite(this.symbolId);

        if (sprite) {
            const position = this.matrixLayer.getPosition(this.symbolId);
            const targetPosition = `symbol_${this.gridPosition.x}_${this.gridPosition.y}`;
            const offset = getOffsetRectangle(this.matrixLayer, "symbol_1_1", targetPosition);

            sprite.x = position.x + offset.x;
            sprite.y = position.y + offset.y;
            sprite.width = position.width;
            sprite.height = position.height;

            CenterPivot(sprite);
        }

        return sprite;
    }

    private updateStatic(transform: Rectangle) {
        // Set correct symbol
        this.staticSprite.visible = false;
        this.staticSprite = this.staticSprites.get(this.symbolId);
        this.staticSprite.visible = true;

        // Update blurred symbol if applicable
        if (this.staticSpriteBlurred) {
            this.staticSpriteBlurred.visible = false;
        }
        this.staticSpriteBlurred = this.staticSpritesBlurred.get(this.symbolId);

        // Update basic transform
        const offset = this.staticSpriteOffsets.get(this.symbolId);

        this.staticSprite.width = transform.width * offset.width;
        this.staticSprite.height = transform.height * offset.height;

        this.staticSprite.x = offset.x + transform.x + this.staticSprite.width * 0.5;
        this.staticSprite.y = offset.y + transform.y + this.staticSprite.height * 0.5;

        // Blurred symbol should match static position
        if (this.staticSpriteBlurred) {
            setTransformRect(this.staticSpriteBlurred, this.staticSprite);
        }
    }

    private updateVisible() {
        this.staticSprite.visible = this.visible && !this.isStackPart;

        // Use blurred static instead of regular one if moving
        if (this.staticSprite.visible && this.blurred && this.staticSpriteBlurred) {
            this.staticSpriteBlurred.visible = true;
            this.staticSprite.visible = false;
        }
    }

    private generateStaticSprites() {

        this.container = new PIXI.Container();
        this.matrixLayer.add(this.container);

        this.layer = this.matrixLayer;

        this.staticSprites = symbolPool.getStaticSpriteMap();
        this.staticSpritesBlurred = symbolPool.getStaticBlurredSpriteMap();
        this.staticSpriteOffsets = new Map<string, Rectangle>();

        for (const symbolDef of SlotDefinition.symbolDefinitions) {
            const sprite = this.staticSprites.get(symbolDef.id);
            this.container.addChild(sprite);

            this.staticSpriteOffsets.set(symbolDef.id, this.getOffsetRectangle(symbolDef.id));

            let staticSpriteBlurred;
            if (GraphicsManager.hasTexture(symbolDef.id + "_blur")) {
                staticSpriteBlurred = this.staticSpritesBlurred.get(symbolDef.id);

                this.container.addChild(staticSpriteBlurred);
            }

            this.staticSprite = sprite;
            this.staticSpriteBlurred = staticSpriteBlurred;
        }
    }

    private getOffsetRectangle(symbolId: string) {
        let offsetRectangle = new Rectangle(0, 0, 1, 1);
        if (this.layer.getPosition(symbolId)) {
            // Symbol specific offsets.
            offsetRectangle = getOffsetRectangle(this.layer, this.masterPosition, symbolId);
        } else if (this.layer.getPosition("static")) {
            // Universal offset.
            offsetRectangle = getOffsetRectangle(this.layer, this.masterPosition, "static");
        }

        return offsetRectangle;
    }
}
